import {
  AnyObject,
  AppCollectionDataTypes,
  AppCollectionsState,
  AppName,
  Collection,
  CollectionId,
  CollectionItem,
  CollectionState,
  DBMetadata,
  DynamicDropdownField,
  Field,
  fieldFormat,
  FieldMapValue,
  FieldTypes,
  formatDropdownValue,
  getCollectionId,
  getDateString,
  getDynamicDropdownOption,
  getDynamicDropdownValue,
  IdField,
  notUndefinedOrNull,
  timeStringFrom24Hour,
  UserGroup,
  WithAlternate,
} from '@hb/shared'
import { useAuth } from '../store/auth'

const getCollectionItem = (
  collections: AppCollectionsState,
  collection: Collection<any>,
  id: string,
) => {
  const collectionId = getCollectionId(collection)
  const collectionState = collections[collectionId] as CollectionState<
    AppCollectionDataTypes[CollectionId]
  >
  if (!collectionState) throw new Error(`Collection ${collectionId} not found`)
  return collectionState.items.find(item => item.id === id)
}

const formatIdField = <T extends AnyObject>(
  value: string | string[],
  collections: AppCollectionsState,
  field: IdField<T>,
) => {
  if (field.withNoneOption && value === 'NULL') return 'None'
  if (field.allowMultiple) {
    if (!Array.isArray(value)) return 'None'
    return value
      .map(v => getCollectionItem(collections, field.collection, v)?.name)
      .filter(Boolean)
      .join(', ')
  }
  if (typeof value !== 'string') return 'None'
  const collectionItem = getCollectionItem(collections, field.collection, value)
  return collectionItem?.name || 'Could not find item'
}

export interface FormatValueProps {
  field?: Field
  value?: any
  parentValue?: FieldMapValue
  collections?: AppCollectionsState
  textOnly?: boolean
}

export const getAlternateString = (value: WithAlternate<string>) => {
  let str = value?.main ?? ''
  if (value.alternate)
    str = str ? `${str} (Alternate: ${value.alternate})` : `(Alternate: ${value.alternate})`
  return str || 'None'
}

export const getHeightFtIn = (height: number) => {
  const feet = Math.floor(height / 12)
  const inches = height % 12
  return { feet, inches }
}

export const formatHeightValue = (value: number) => {
  if (!value) return 'None'
  const { feet, inches } = getHeightFtIn(value)
  return `${feet}' ${inches}"`
}

export const formatValue = ({ field, value, collections, parentValue }: FormatValueProps) => {
  if (field?.format) return field.format(value)
  switch (field?.type) {
    case FieldTypes.ID:
      if (!value) return 'None'
      if (!collections) return 'Could not find item'
      if (!collections[getCollectionId(field.collection)]) return 'Could not find item'
      return formatIdField(value, collections, field)

    case FieldTypes.DYNAMIC_DROPDOWN:
      return getDynamicDropdownValue({
        value,
        dynamicDropdownOptions: getDynamicDropdownOptions(
          collections ?? {},
          field,
          parentValue,
          useAuth.getState().admin,
        ),
      })
    case FieldTypes.HEIGHT:
      return formatHeightValue(value)
    case FieldTypes.DROPDOWN:
      return formatDropdownValue(value, field.options) || 'None'
    case FieldTypes.DATE:
      if (!value) return 'None'
      return getDateString(value, 'short')
    case FieldTypes.TIME:
      if (value === undefined || typeof value !== 'string') return 'None'
      return timeStringFrom24Hour(value.split('-')[0] ?? '')
    case FieldTypes.ALTERNATE:
      if (value === undefined) return 'None'
      if (typeof value !== 'object') return value
      return getAlternateString(value)
    case FieldTypes.CHECKBOX:
      return value ? 'Yes' : 'No'
    default:
      if (!notUndefinedOrNull(value)) return 'None'
      if (field?.type) {
        const format = fieldFormat[field.type]
        const formatted = format?.(value, field, collections ?? {})
        if (formatted) return typeof formatted === 'string' ? formatted : ''
      }
      return typeof value === 'string' ? value : ''
  }
}

const getMetadataUserGroup = (appName: AppName) => {
  const { admin, claims } = useAuth.getState()
  const isPracticeMember = !!Object.keys(claims?.practiceAccess ?? {}).length
  let userGroup: UserGroup = 'patient'
  if (appName === 'providers-app' && isPracticeMember) {
    userGroup = 'practice'
  } else if (admin) {
    userGroup = 'admin'
  }
  return userGroup
}

export const baseGetMetadata = (isNew: boolean, uid: string, userGroup: UserGroup) => {
  const metadata: Partial<DBMetadata> = {
    updatedBy: uid,
    updatedOn: Date.now(),
    updatedByGroup: userGroup,
  }

  if (isNew) {
    metadata.createdBy = uid
    metadata.createdOn = Date.now()
    metadata.createdByGroup = userGroup
  }

  return metadata as DBMetadata
}

export const baseGetCreatedMetadata = (uid: string, userGroup: UserGroup) => ({
  createdBy: uid,
  createdOn: Date.now(),
  createdByGroup: userGroup,
})

export const getMetadata = (appName: AppName, isNew: boolean, userGroup?: UserGroup) => {
  const { authUser } = useAuth.getState()
  if (!authUser) throw new Error('Not logged in')

  const updatedByGroup = userGroup ?? getMetadataUserGroup(appName)
  return baseGetMetadata(isNew, authUser.uid, updatedByGroup)
}

export const getCreatedMetadata = (appName: AppName, userGroup?: UserGroup) => {
  const { authUser } = useAuth.getState()
  if (!authUser) throw new Error('Not logged in')

  const createdByGroup = userGroup ?? getMetadataUserGroup(appName)

  return baseGetCreatedMetadata(authUser.uid, createdByGroup)
}

export const addMetadata = <T extends AnyObject>(
  data: T,
  appName: AppName,
  isNew: boolean,
  userGroup?: UserGroup,
) => ({
  ...data,
  ...getMetadata(appName, isNew, userGroup),
})

export const addCreatedMetadata = <T extends AnyObject>(
  data: T,
  appName: AppName,
  userGroup?: UserGroup,
) => ({
  ...data,
  ...getCreatedMetadata(appName, userGroup),
})

export const getDynamicDropdownOptions = (
  collections: AppCollectionsState,
  field: DynamicDropdownField<any>,
  rootValue?: FieldMapValue,
  userIsAdmin?: boolean,
) => {
  const { baseId, collection, listId } = field
  const itemId = baseId && rootValue ? rootValue[baseId] : undefined
  const collectionId = getCollectionId(collection)
  const collectionState = collections[collectionId] as CollectionState<
    AppCollectionDataTypes[CollectionId]
  >
  if (!collectionState) return []
  const item = collectionState.items.find(i => i.id === itemId)

  if (item && listId) {
    const indexable = item as Record<string, any>
    if (indexable[listId]) {
      if (indexable[listId] instanceof Array) {
        return indexable[listId].reduce<Array<CollectionItem<any>>>(
          (acc: Array<CollectionItem<any>>, listItem: CollectionItem<any>) => {
            if (!listItem.isInactive || userIsAdmin) {
              return [...acc, getDynamicDropdownOption(listItem)]
            }
            return acc
          },
          [],
        )
      }

      console.error('Dynamic dropdown options is not an array')
    }
  }
  return []
}
